import React, {
    memo,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import {
    Flex,
    FlexProps,
    Grid,
    Text,
} from '@chakra-ui/react';
import { ArticleCard, RichText, useAppDispath } from 'shared';
import { useParams, useSearchParams } from 'react-router-dom';
import { LeadSmallSection, useCart } from 'features';
// eslint-disable-next-line no-restricted-imports
import { Tabs } from 'shared/ui/Tabs';
import { SweetShopArticleCategoryTagModel, SweetShopArticleModel } from 'entities';
import { Helmet } from 'react-helmet-async';
// eslint-disable-next-line no-restricted-imports
import { richTextToString } from 'shared/lib/richtext-to-string';
import {
    SweetShopArticlesPageMainHeader,
} from './SweetShopArticlesPageMainHeader';
import {
    useSweetShopArticlesPageState,
    SweetShopArticlesPageActions,
} from '../../store';

/**
 * Функция, которая создаёт компонент лейбла для таба
 *
 * @param {string} label Текст
 * @returns {JSX.Element} Компонент
 */
const createSweetShopArticlesTabsItemLabel = (
    label: string,
) => (
    <Text fontSize="1.5rem">{label.toLocaleUpperCase()}</Text>
);

/**
 * Функция, которая создаёт компонент контента в табе
 *
 * @param {SweetShopArticleModel[]} sweetShopArticles Список позиций кондитерской
 * @returns {JSX.Element} Компонент
 */
const createSweetShopArticlesTabsContentItem = (
    sweetShopArticles: SweetShopArticleModel[],
) => (
    <Grid
        key="all"
        templateColumns={{
            base: '1fr 1fr',
            lg: '1fr 1fr 1fr',
        }}
        gap="2rem"
    >
        {sweetShopArticles.map(({
            uid,
            name: name2,
            description,
            images,
            price,
            weight,
            currency,
        }) => (
            <ArticleCard
                type="sweet-shop"
                uid={uid}
                name={name2}
                description={{
                    content: description,
                }}
                image={images?.at(0)}
                price={price || undefined}
                weight={weight || undefined}
                currencyAbbreviation={currency?.abbreviation || undefined}
                link={{ url: `/sweet-shop/articles/${uid}` }}
            />
        ))}
    </Grid>
);

/**
 * Функция, которая создаёт список компонентов для табов
 *
 * @param {SweetShopArticleModel[]} sweetShopArticles Список позиций кондитерской
 * @param {SweetShopArticleCategoryTagModel[]} sweetShopArticleCategoryTags
 * Список тегов для текущей категории
 * @returns {JSX.Element} Параметр items в TabsProps
 */
const createSweetShopArticlesTabsItems = (
    sweetShopArticles: SweetShopArticleModel[],
    sweetShopArticleCategoryTags: SweetShopArticleCategoryTagModel[],
) => ([{
    key: 'all',
    labelComponent: createSweetShopArticlesTabsItemLabel('ВСЕ'),
    contentComponent: createSweetShopArticlesTabsContentItem(sweetShopArticles),
}].concat(
    sweetShopArticleCategoryTags.map(({
        uid,
        name,
    }) => ({
        key: uid,
        labelComponent: createSweetShopArticlesTabsItemLabel(name),
        contentComponent: createSweetShopArticlesTabsContentItem(sweetShopArticles),
    })),
));

/**
 * Мемоизированный хедер блока, тк как он не перерисовывается
 */
const MemoHeader = memo(SweetShopArticlesPageMainHeader);

/**
 * Пропсы для компонента блока позиций кондитерской
 */
export type SweetShopArticlesPageMainProps = FlexProps & React.RefAttributes<HTMLDivElement>

/**
 * Компонент блока позиций кондитерской
 * @param param0
 * @returns {JSX.Element}
 */
export const SweetShopArticlesPageMain = ({
    ...props
}: SweetShopArticlesPageMainProps) => {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        sweetShopArticleCategory,
        sweetShopArticleCategoryTags,
        sweetShopArticles,
        leadFormPdf,
    } = useSweetShopArticlesPageState();
    const dispatch = useAppDispath();

    const { items } = useCart();

    /**
     * Ключ активного таба
     */
    const [currentKey, setCurrentKey] = useState<string>();

    useLayoutEffect(() => {
        if (id) {
            dispatch(SweetShopArticlesPageActions.getSweetShopArticleCategoryByUid({
                uid: id,
            }));
            dispatch(SweetShopArticlesPageActions.getSweetShopArticleCategoryTagsByCategoryUid({
                categoryUid: id,
            }));
        }
    }, [id]);

    useEffect(() => {
        if (searchParams.get('tab') !== currentKey) {
            setCurrentKey(searchParams.get('tab') || 'all');
        }
    }, [searchParams.get('tab')]);

    useEffect(() => {
        if (currentKey && id) {
            dispatch(SweetShopArticlesPageActions.getSweetShopArticlesByCategoryUidAndTagUid({
                categoryUid: id,
                tagUid: currentKey !== 'all' ? currentKey : undefined,
            }));
            if (currentKey !== searchParams.get('tab')) {
                setSearchParams({
                    tab: currentKey,
                });
            }
        }
    }, [currentKey]);

    /**
     * Мемоизированные табы
     */
    const memoTabsItems = useMemo(() => (
        createSweetShopArticlesTabsItems(
            sweetShopArticles,
            sweetShopArticleCategoryTags,
        )
    ), [sweetShopArticles, sweetShopArticleCategoryTags, items]);

    return (
        <Flex
            {...props}
            flexDirection="column"
        >
            <Helmet>
                <title>{sweetShopArticleCategory?.name}</title>
                <meta name="description" content={richTextToString(sweetShopArticleCategory?.description)} />
                <meta property="og:title" content={sweetShopArticleCategory?.name} />
            </Helmet>
            <MemoHeader
                name={sweetShopArticleCategory?.name || ''}
                description={sweetShopArticleCategory?.description}
                imageSrc={sweetShopArticleCategory?.image?.url || ''}
            />
            <Grid
                background="#000000"
            >
                <LeadSmallSection
                    title="НЕ МОЖЕТЕ ОПРЕДЕЛИТЬСЯ?"
                    padding="2rem"
                    buttonText="Заказать звонок"
                    background="#000000"
                    maxWidth={{
                        base: '425px',
                        lg: '1280px',
                    }}
                    width="100%"
                    margin="0 auto"
                    pdfUrl={leadFormPdf?.url}
                />
            </Grid>
            <Flex
                flexDirection="column"
                padding="2rem"
                gap="2rem"
                maxWidth={{
                    base: '425px',
                    lg: '1280px',
                }}
                background="white"
                width="100%"
                margin="0 auto"
            >
                <RichText
                    content={sweetShopArticleCategory?.addictionalDescription}
                    fontSize="2rem"
                />
                <Tabs
                    value={currentKey}
                    onValueChange={(v) => setCurrentKey(v)}
                    items={memoTabsItems}
                />
            </Flex>
        </Flex>
    );
};
